// Stream filters (ISO 32000-1 §7.4): Flate, LZW, ASCII85, ASCIIHex, RunLength with their predictors. Image codecs (DCT, JPX, CCITT,
// JBIG2) stop the chain: the image decoder takes what is left.

import { inflate } from "./inflate";
import { Dict, PName, Stream, type PObj } from "./parse";

export interface Decoded {
    data: Uint8Array;
    /** The image filter that is still to be applied ("DCTDecode" …), with its parameters. */
    image: string | null;
    parms: Dict | null;
}

const ALIASES: Record<string, string> = { Fl: "FlateDecode", LZW: "LZWDecode", A85: "ASCII85Decode", AHx: "ASCIIHexDecode", RL: "RunLengthDecode", DCT: "DCTDecode", CCF: "CCITTFaxDecode" };

function ascii85(src: Uint8Array): Uint8Array {
    const out: number[] = [];
    let n = 0;
    const g = [0, 0, 0, 0, 0];
    for (let i = 0; i < src.length; i++) {
        const c = src[i]!;
        if (c === 0x7e) break; // "~>"
        if (c <= 32) continue;
        if (c === 0x7a && n === 0) { out.push(0, 0, 0, 0); continue; }
        if (c < 33 || c > 117) continue;
        g[n++] = c - 33;
        if (n === 5) {
            const v = ((((g[0]! * 85 + g[1]!) * 85 + g[2]!) * 85 + g[3]!) * 85 + g[4]!) >>> 0;
            out.push((v >>> 24) & 255, (v >>> 16) & 255, (v >>> 8) & 255, v & 255);
            n = 0;
        }
    }
    if (n > 1) {
        for (let i = n; i < 5; i++) g[i] = 84;
        const v = ((((g[0]! * 85 + g[1]!) * 85 + g[2]!) * 85 + g[3]!) * 85 + g[4]!) >>> 0;
        const b = [(v >>> 24) & 255, (v >>> 16) & 255, (v >>> 8) & 255, v & 255];
        for (let i = 0; i < n - 1; i++) out.push(b[i]!);
    }
    return Uint8Array.from(out);
}

function asciiHex(src: Uint8Array): Uint8Array {
    const out: number[] = [];
    let hi = -1;
    for (const c of src) {
        if (c === 0x3e) break;
        const v = c >= 0x30 && c <= 0x39 ? c - 0x30 : c >= 0x41 && c <= 0x46 ? c - 0x37 : c >= 0x61 && c <= 0x66 ? c - 0x57 : -1;
        if (v < 0) continue;
        if (hi < 0) hi = v; else { out.push(hi * 16 + v); hi = -1; }
    }
    if (hi >= 0) out.push(hi * 16);
    return Uint8Array.from(out);
}

function runLength(src: Uint8Array): Uint8Array {
    const out: number[] = [];
    let i = 0;
    while (i < src.length) {
        const l = src[i++]!;
        if (l === 128) break;
        if (l < 128) { for (let k = 0; k <= l && i < src.length; k++) out.push(src[i++]!); }
        else { const v = src[i++]!; for (let k = 0; k < 257 - l; k++) out.push(v); }
    }
    return Uint8Array.from(out);
}

function lzw(src: Uint8Array, early: number): Uint8Array {
    const out: number[] = [];
    let table: number[][] = [];
    const reset = () => { table = []; for (let i = 0; i < 256; i++) table.push([i]); table.push([], []); };
    reset();
    let bits = 9, buf = 0, cnt = 0, prev: number[] | null = null;
    for (let i = 0; i < src.length; i++) {
        buf = (buf << 8) | src[i]!;
        cnt += 8;
        while (cnt >= bits) {
            const code = (buf >> (cnt - bits)) & ((1 << bits) - 1);
            cnt -= bits;
            buf &= (1 << cnt) - 1;
            if (code === 256) { reset(); bits = 9; prev = null; continue; }
            if (code === 257) return Uint8Array.from(out);
            let entry: number[];
            if (code < table.length) entry = table[code]!;
            else if (prev) entry = [...prev, prev[0]!];
            else continue;
            for (const v of entry) out.push(v);
            if (prev) table.push([...prev, entry[0]!]);
            prev = entry;
            const size = table.length + early;
            bits = size >= 2048 ? 12 : size >= 1024 ? 11 : size >= 512 ? 10 : 9;
        }
    }
    return Uint8Array.from(out);
}

function predictor(data: Uint8Array, parms: Dict | null): Uint8Array {
    const pred = parms?.num("Predictor", 1) ?? 1;
    if (pred <= 1) return data;
    const colors = parms!.num("Colors", 1), bpc = parms!.num("BitsPerComponent", 8), columns = parms!.num("Columns", 1);
    const bpp = Math.max(1, (colors * bpc + 7) >> 3);
    const rowBytes = (columns * colors * bpc + 7) >> 3;
    if (pred === 2) {
        if (bpc === 8) {
            const out = data.slice();
            for (let r = 0; r + rowBytes <= out.length; r += rowBytes) for (let i = bpp; i < rowBytes; i++) out[r + i] = (out[r + i]! + out[r + i - bpp]!) & 255;
            return out;
        }
        return data;
    }
    const rows = Math.floor(data.length / (rowBytes + 1)) + (data.length % (rowBytes + 1) ? 1 : 0);
    const out = new Uint8Array(rows * rowBytes);
    let prev = new Uint8Array(rowBytes);
    for (let r = 0; r < rows; r++) {
        const s = r * (rowBytes + 1);
        const ft = data[s]!;
        const cur = out.subarray(r * rowBytes, (r + 1) * rowBytes);
        const line = data.subarray(s + 1, Math.min(data.length, s + 1 + rowBytes));
        cur.set(line);
        switch (ft) {
            case 1: for (let i = bpp; i < rowBytes; i++) cur[i] = (cur[i]! + cur[i - bpp]!) & 255; break;
            case 2: for (let i = 0; i < rowBytes; i++) cur[i] = (cur[i]! + prev[i]!) & 255; break;
            case 3: for (let i = 0; i < rowBytes; i++) cur[i] = (cur[i]! + (((i >= bpp ? cur[i - bpp]! : 0) + prev[i]!) >> 1)) & 255; break;
            case 4:
                for (let i = 0; i < rowBytes; i++) {
                    const a = i >= bpp ? cur[i - bpp]! : 0, b = prev[i]!, c = i >= bpp ? prev[i - bpp]! : 0;
                    const p = a + b - c, pa = Math.abs(p - a), pb = Math.abs(p - b), pc = Math.abs(p - c);
                    cur[i] = (cur[i]! + (pa <= pb && pa <= pc ? a : pb <= pc ? b : c)) & 255;
                }
                break;
        }
        prev = cur;
    }
    return out;
}

/** Applies the stream's filters. With `keepImage`, nothing is decoded past an image filter either way: it is reported in `image`. */
export function decodeStream(s: Stream, _keepImage: boolean): Decoded {
    const d = s.dict;
    let f = d.get("Filter", "F");
    let p = d.get("DecodeParms", "DP");
    const filters: PObj[] = Array.isArray(f) ? f : f instanceof PName ? [f] : [];
    const parms: PObj[] = Array.isArray(p) ? p : p ? [p] : [];
    let data = s.raw;
    for (let i = 0; i < filters.length; i++) {
        const raw = filters[i];
        const fo = raw && typeof raw === "object" && "name" in (raw as object) ? (raw as PName) : null;
        const name = fo ? ALIASES[fo.name] ?? fo.name : "";
        const pr = parms[i] instanceof Dict ? (parms[i] as Dict) : null;
        switch (name) {
            case "FlateDecode": data = predictor(inflate(data), pr); break;
            case "LZWDecode": data = predictor(lzw(data, pr ? pr.num("EarlyChange", 1) : 1), pr); break;
            case "ASCII85Decode": data = ascii85(data); break;
            case "ASCIIHexDecode": data = asciiHex(data); break;
            case "RunLengthDecode": data = runLength(data); break;
            case "Crypt": break;
            case "DCTDecode": case "JPXDecode": case "CCITTFaxDecode": case "JBIG2Decode":
                return { data, image: name, parms: pr };
            default: break;
        }
    }
    return { data, image: null, parms: null };
}
